import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isRedacted } from './redaction';

export { REDACTION_MARKER, isRedacted } from './redaction';

/**
 * Message for a field whose value we destroyed on the retention schedule. Deliberately does not
 * say "invalid": the guest did supply this once, and we removed it.
 */
export const validateNotRedacted = value =>
  (isRedacted(value) ? 'We no longer hold this — please enter it again.' : null);

const BLOCKED_PASSPORT_VALUES = new Set([
  '123456', '1234567', '12345678', '123456789', '1234567890',
  '0123456', '0123456789', '654321', '0987654321', '987654321', '9876543210'
]);

// Passport numbers hold at most 9 characters (ICAO 9303), and short digit-only values are
// ambiguous: most 8-9 digit passports are also valid national phone numbers in some country.
// Only values too long to be a passport number are worth testing against libphonenumber.
const PHONE_MIN_DIGITS_WITHOUT_PREFIX = 11;

const normalizePassport = value => (value || '').replace(/\s/g, '').toUpperCase();

export const looksLikePhoneNumber = (value, defaultCountry) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return false;
  }
  if (/[a-zA-Z]/.test(trimmed)) {
    return false;
  }
  if (trimmed.startsWith('+')) {
    return true;
  }
  // Passport numbers never contain dialling brackets.
  if (/[()]/.test(trimmed)) {
    return true;
  }
  if (trimmed.replace(/\D/g, '').length < PHONE_MIN_DIGITS_WITHOUT_PREFIX) {
    return false;
  }
  try {
    if (isValidPhoneNumber(trimmed, defaultCountry || undefined)) {
      return true;
    }
  } catch {
    // ignore parse errors
  }

  return false;
};

export const validatePassport = (value, nationality) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return null;
  }
  if (isRedacted(trimmed)) {
    return validateNotRedacted(trimmed);
  }

  if (looksLikePhoneNumber(trimmed, nationality)) {
    return 'Please enter your passport number, not a phone number.';
  }

  const normalized = normalizePassport(trimmed);

  if (/(.)\1{5,}/.test(normalized)
    || !/^(?=.*\d)\w{6,}$/.test(normalized)
    || BLOCKED_PASSPORT_VALUES.has(normalized)
  ) {
    return 'This passport number is not valid.';
  }

  return null;
};

export const validatePhone = (value, defaultCountry) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return null;
  }
  if (isRedacted(trimmed)) {
    return validateNotRedacted(trimmed);
  }
  try {
    const phone = parsePhoneNumberFromString(trimmed, defaultCountry || undefined);
    if (!phone || !phone.isValid()) {
      return 'Please enter a valid international phone number.';
    }
    return null;
  } catch {
    return 'Please enter a valid international phone number.';
  }
};
